/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */
'use strict';

/**
 * This is an extension of the MediumEditor library that allows us to modify and configure their Smart Paste
 * implementation in full. We can configure what is pasted, what is allowed, what is stripped from the paste and more
 * inside of this factory.
 */
angular.module('bulbApp.contentEditorModule')
.factory('BulbPasteExtensionFactory', [
  '$timeout', '$translate', 'MediumEditor', 'Restangular', 'BulbAuthService',
  'BulbContentEditorService', 'BulbErrorService', 'BulbModalService',
  BulbPasteExtensionFactory
]);

function BulbPasteExtensionFactory($timeout, $translate, MediumEditor, Restangular, BulbAuthService,
                        BulbContentEditorService, BulbErrorService, BulbModalService) {

  // We use this function to set the user preference "hideSmartPasteModal" in the Account settings so we have it
  // for use, instead of doing cookies to set the modal preference.
  function setModalHideFlag() {
    Restangular.one('accounts', BulbAuthService.currentUser.id)
      .patch({ prefs : { hideSmartPasteModal : true }})
      .then(function (updatedAccount) {
        BulbAuthService.setCurrentUser(updatedAccount);
      })
      .catch(function(error) {
        BulbErrorService.error('Cannot update the user preferences', error);
      });
  }

  var pasteTranslations = $translate.instant(['pageview_paste_missing_table_text', 'pageview_paste_missing_image_text',
    'pageview_paste_missing_video_text', 'pageview_paste_missing_file_text',
    'pageview_paste_missing_embed_text', 'pageview_paste_missing_carousel_text']);

  // Medium Editor's function to do replacements during the paste using regex, any future replacements
  // that need to be made during paste should be put here in the format of ['item to replace', 'replace with'].
  function createReplacements() {
    return [
      // Remove anything in the paste but the pure contents of the <body> tag.
      [new RegExp(/^[\s\S]*<body[^>]*>\s*|\s*<\/body[^>]*>[\s\S]*$/g), ''],

      // This cleans up any comments that were added by Chrome when pasting HTML in the browser.
      [new RegExp(/<!--StartFragment-->|<!--EndFragment-->/g), ''],

      // Should eliminate most/all of the trailing break (<br />) tags from appearing in the paste.
      [new RegExp(/<br>$/i), ''],

      // Google docs has a problem where it wraps the copy in a weird <b> tag and strange <br /> tags. Fix here.
      [new RegExp(/<[^>]*docs-internal-guid[^>]*>/gi), ''],
      [new RegExp(/<\/b>(<br[^>]*>)?$/gi), ''],

      // Apple (OSX) has it's own spaces/newlines sometimes, convert those to the appropriate HTML counterparts.
      [new RegExp(/<span class="Apple-converted-space">\s+<\/span>/g), ' '],
      [new RegExp(/<br class="Apple-interchange-newline">/g), '<br>'],

      // We just need to, in general, replace italic/bold spans with the correct things.
      [new RegExp(/<span style="font-weight: bold;">/g), '<span class="replace-with bold">'],
      [new RegExp(/<span style="font-style: italic;">/g), '<span class="replace-with italic">'],
      [new RegExp(/<span style="text-decoration: underline;">/g), '<span class="replace-with underline">'],

      // Google Docs italics+bold needs to be replaced with a span with an indication that it should be changed later.
      [new RegExp(/<span[^>]*(font-style:italic;font-weight:(bold|700)|font-weight:(bold|700);font-style:italic)[^>]*>/gi),
        '<span class="replace-with italic bold">'],

      // Handles ONLY italics words on Google Docs, not bold.
      [new RegExp(/<span[^>]*font-style:italic[^>]*>/gi), '<span class="replace-with italic">'],

      // Handles ONLY bold words on Google Docs, not italics.
      [new RegExp(/<span[^>]*font-weight:(bold|700)[^>]*>/gi), '<span class="replace-with bold">'],

      // Handles ONLY underlined words on Google Docs, not bold/italics.
      [new RegExp(/<span[^>]*text-decoration:underline[^>]*>/gi), '<span class="replace-with underline">'],

      // Any manually entered <i><b> or <a> tags should be replaced with their real counterparts here.
      [new RegExp(/&lt;(\/?)(i|b|a)&gt;/gi), '<$1$2>'],

      // Manually replaces tags with real ones, while converting "smart-quotes" from Google Docs.
      [new RegExp(/&lt;a(?:(?!href).)+href=(?:&quot;|&rdquo;|&ldquo;|"|“|”)(((?!&quot;|&rdquo;|&ldquo;|"|“|”).)*)(?:&quot;|&rdquo;|&ldquo;|"|“|”)(?:(?!&gt;).)*&gt;/gi), '<a href="$1">'],

      // Newlines between paragraphs in HTML have no syntactic value, but become additional paragraphs later.
      [new RegExp(/<\/p>\n+/gi), '</p>'],
      [new RegExp(/\n+<p/gi), '<p'],

      // Microsoft Word makes very strange tags sometimes, like: <o:p></o:p>. We'll remove those.
      [new RegExp(/<\/?o:[a-z]*>/gi), ''],

      // Microsoft Word also adds special elements around list items, replace those with the appropriate <li>.
      [new RegExp(/<!\[if !supportLists\]>(((?!<!).)*)<!\[endif]\>/gi), '$1']
    ];
  }

  var BulbPasteExtension = MediumEditor.extensions.paste.extend({

    cleanedImagesOnPaste: false,

    /*
     * @cleanPaste
     * We overwrite this method because we want access to the elements prior to their cleaning by MediumEditor,
     * since we want to approve certain things like bulb-approved spans and the removal of images with a modal
     * that shows they were removed.
     *
     * We also handle content-blocks (as a custom tag) in this function, since copy and pasting from one bulb page
     * to the other must be supported by the Smart Paste functionality.
     */
    cleanPaste: function (text) {

      this.resetFormattingBeforePaste();

      var i, elList, tmp, workEl,
        multiline = /<p|<br|<div/.test(text),
        replacements = [].concat(
          this.preCleanReplacements || [],
          createReplacements(),
          this.cleanReplacements || []);

      for (i = 0; i < replacements.length; i += 1) {
        text = text.replace(replacements[i][0], replacements[i][1]);
      }

      // Reset the default value on each paste.
      this.cleanedImagesOnPaste = false;
      this.contentBlocksOnPaste = [];

      if (!multiline) {
        return this.pasteHTML(text);
      }

      // For some reason, images are different depending on if they're wrapped in <p><span> or not. Fix that here.
      var  $text = $(jQuery.parseHTML('<div>' + text + '</div>'));
      var imagesInHTML = $text.find('img');

      for (var j = 0; j < imagesInHTML.length; j++) {
        var currentImage = $(imagesInHTML[j]);

        // Some images are links on webpages, so if it's a link, we need to remove it and then show that
        // we don't support images.
        if (currentImage.parent().prop('tagName').toLowerCase() === 'a') {
          currentImage.unwrap();
        }

        $(currentImage).replaceWith('<b>[' +
          pasteTranslations['pageview_paste_missing_image_text'] + ']</b>'); // jshint ignore: line
        this.cleanedImagesOnPaste = true;
      }

      var contentBlocksInHTML = $text.find('content-block');

      for (var k = 0; k < contentBlocksInHTML.length; k++) {
        var definingBlockAttr = $(contentBlocksInHTML[k]).children().children().first().prop('tagName');
        // We only need the type of the block (such as <image-block>) when pushing content-blocks from other
        // pages through to the paste functionality below. Anything else can be scrapped.
        $(contentBlocksInHTML[k]).attr('bulb-tag-name', definingBlockAttr);
      }

      // Now that we made our changes, let's set it back to the text for processing.
      text = $text.html();

      // Create a temporary <div> element to clean-up block elements.
      tmp = this.document.createElement('div');

      // As of now, double <br /> tags aren't converted to <p> tags, but we want <p> tags.
      tmp.innerHTML = '<p>' + text.split('<br><br>').join('</p><p>') + '</p>';

      // This will clean up specific block elements in the paste.
      elList = tmp.querySelectorAll('a, p, div, br');
      for (i = 0; i < elList.length; i += 1) {
        workEl = elList[i];

        // Microsoft Word replaces some spaces with newlines. While newlines between block elements are
        // meaningless, newlines within elements are sometimes actually spaces.
        workEl.innerHTML = workEl.innerHTML.replace(/\n/gi, ' ');

        switch (workEl.nodeName.toLowerCase()) {
          case 'p':
          case 'div':
            this.filterCommonBlocks(workEl);
            break;
          case 'br':
            this.filterLineBreak(workEl);
            break;
        }
      }

      this.pasteHTML(tmp.innerHTML);
    },

    /*
     * @pasteHTML
     * This is where actual clean-up occurs after the HTML has been sanitized by cleanPaste. It loops through each
     * element, giving it a chance to be changed or altered depending on what we want. In this function we must
     * overwrite it to handle tags that don't exist on bulb, but are allowed through the clean, such as H1/H5 tags,
     * and content-blocks.
     */
    pasteHTML: function (html, options) {
      options = MediumEditor.util.defaults({}, options, {
        cleanAttrs: this.cleanAttrs,
        cleanTags: this.cleanTags
      });

      var elList, workEl, i, fragmentBody, pasteBlock = this.document.createDocumentFragment();

      pasteBlock.appendChild(this.document.createElement('body'));

      fragmentBody = pasteBlock.querySelector('body');
      fragmentBody.innerHTML = html;

      this.cleanupSpans(fragmentBody);

      elList = fragmentBody.querySelectorAll('*');

      // iterating elList in reverse in order to replace child elements first and then move to parent elements
      for (i = elList.length - 1; i >= 0 ; i--) {
        workEl = elList[i];

        // We can run the element through our custom checks first.
        this.handleTagInPaste(workEl);

        // After all is said and done, let's see if there any tags that somehow managed to get through
        // that can't be used on our editor before pasting. If there's text inside, we can convert it to a <p>
        // tag.
        if (!BulbContentEditorService.isAllowedTag(workEl)) {
          if ($(workEl).text() !== '') {
            $(workEl).replaceWith('<p>' + $(workEl).html() + '</p>');
          } else {
            $(workEl).remove();
          }
        }
      }

      // cleaning html to remove unnecessary empty p tags
      const cleanedHTML = fragmentBody.innerHTML.replace(/<p><\/p>/g, '').replace(/&nbsp;/g, ' ');
      MediumEditor.util.insertHTMLCommand(this.document, cleanedHTML);

      // If we removed images from their paste, we need to make sure that we show a modal to the user explaining
      // that bulb doesn't support paste at this time, and etc.
      if (this.cleanedImagesOnPaste && !BulbAuthService.currentUser.prefs.hideSmartPasteModal) {
        // Do a timeout to make sure the insertHTMLCommand is done before showing the modal.
        $timeout(function() {
          var modalOptions = {
            titleCode: 'pageview_paste_modal_title',
            // bodyCode: 'pageview_paste_modal_body',
            templateUrl: 'ajs/components/contenteditor/mediumeditor/paste-modal.html',
            windowClass: 'smart-paste-modal-styles',
            showOkButton: true,
            showCancelButton: false
          };

          BulbModalService.show(modalOptions).then().finally(function() {
            var wasChecked = $('#disablePasteNotice').is(':checked');

            // We need to set a cookie to not show the modal again if they checked the box.
            if (wasChecked) {
              setModalHideFlag();
            }
          });
        });
      }
    },

    /*
     * @cleanupSpans
     * 90% of this code is default to MediumEditor, but we must use our ContentEditorService to manage what spans
     * are allowed to stay inside of the paste. We don't want any weird spans making it through, but any spans
     * that were created from bulb-pages need to stay, so that font colors/families can be converted over to a new
     * page via Smart Paste. <bulb> dictates where the work began, and </bulb> is the end of the work.
     */
    cleanupSpans: function (containerEl) {
      var i,
        el,
        newEl,
        spans = containerEl.querySelectorAll('.replace-with'),
        isCEF = function (el) {
          return (el && el.nodeName !== '#text' && el.getAttribute('contenteditable') === 'false');
        };

      for (i = 0; i < spans.length; i += 1) {
        el = spans[i];
        newEl = this.document.createElement(el.classList.contains('bold') ? 'b' :
          el.classList.contains('italic') ? 'i' : 'u');

        if (el.classList.contains('bold') && el.classList.contains('italic') &&
          el.classList.contains('underline')) {
          // add an i tag as well if this has both italics and bold
          newEl.innerHTML = '<i><u>' + el.innerHTML + '</u></i>';
        } else if (el.classList.contains('bold') && el.classList.contains('italic')) {
          newEl.innerHTML = '<i>' + el.innerHTML + '</i>';
        } else {
          newEl.innerHTML = el.innerHTML;
        }

        el.parentNode.replaceChild(newEl, el);
      }

      spans = containerEl.querySelectorAll('span');
      for (i = 0; i < spans.length; i += 1) {
        el = spans[i];

        // MediumEditor function to see if we're in a "non-content-editable" block. We need to bail.
        if (MediumEditor.util.traverseUp(el, isCEF)) {
          return false;
        }

        // <bulb>
        var allowSpanToStay = false;

        // Remove all span styles except for ones accepted by bulb.
        var keepStyles = {
          'font-family': null,
          'color': null
        };

        // Checks to make sure the element has an acceptable font family style.
        var bulbFontCheck = BulbContentEditorService.hasBulbFont(el);
        if (bulbFontCheck.accepted === true) {
          keepStyles['font-family'] = bulbFontCheck.formattedFont;
          allowSpanToStay = true;
        }

        // Same thing here, checking if the span coming in from the paste has an acceptable color that is
        // approved for usage on bulb.
        var bulbColorCheck = BulbContentEditorService.hasBulbColor(el);
        if (bulbColorCheck.accepted === true) {
          keepStyles.color = bulbColorCheck.colorInHEX;
          allowSpanToStay = true;
        }

        // Remove all span styles, entirely. But we will save the color/font-family if they were approved for
        // usage on bulb's content editor.
        $(el).removeAttr('style').css(keepStyles);

        // We basically say here that if either the font-family or font-color was acceptable via bulb, we allow
        // the span to stay in the paste. Not just instantly unwrap it.
        if (allowSpanToStay) {
          continue;
        }
        // </bulb>

        // Unwrap the spans that were not approved via the process above.
        MediumEditor.util.unwrap(el, this.document);
      }
    },

    handleTagInPaste: function (workEl) {

      switch (workEl.nodeName.toLowerCase()) {
        // These are tags that need to be replaced visually with something else since they do not work in our
        // paste and editor. Usually replaced by a tag that just shows it wasn't ported over.
        case 'content-block':
        case 'table':
          this.handleReplacementInPaste(workEl);
          break;
        // There should never be a "PRE" nor a "DIV" tag, but both of these usually store some kind of text.
        // Let's wrap them in our user-friendly <p> tags, and remove the original.
        case 'pre':
        case 'div':
          $(workEl).replaceWith('<p>' + $(workEl).html() + '</p>');
          break;
        // MediumEditor's default handling for anchor tags. It works as expected.
        case 'a':
          MediumEditor.util.setTargetBlank(workEl);
          break;
        // Smart Paste somehow puts list items in <p> tags, unwrap them here..
        case 'li':
          $(workEl).find('p').contents().unwrap();
          break;
        // Bulb does not support h1 or h4, so we need to convert them to the next convenient size.
        case 'h1':
        case 'h4':
        case 'h5':
        case 'h6':
          this.handleHeadingElementsOnPaste(workEl);
          break;
      }

      // We clean up spans individually, but every other tag needs to be cleaned up.
      if ('span' !== workEl.nodeName.toLowerCase()) {
        this.removeAllStyles(workEl);
      }
    },

    handleReplacementInPaste: function (workEl) {
      // Content blocks do not work when pasted, so remove them here, leaving a message. Also set this at the
      // top, since if it's a content-block, we want to handle it here and not have to worry about the other
      // checkpoints in this for loop. Neither do tables, so let's replace them.
      /* jshint ignore: start */
      var blockType = $(workEl).attr('bulb-tag-name') || workEl.nodeName;
      var translation = '';

      switch (blockType.toLowerCase()) {
        case 'table':
          translation = pasteTranslations['pageview_paste_missing_table_text'];
          break;
        case 'image-block':
          translation = pasteTranslations['pageview_paste_missing_image_text'];
          break;
        case 'video-block':
          translation = pasteTranslations['pageview_paste_missing_video_text'];
          break;
        case 'attached-file-block':
          translation = pasteTranslations['pageview_paste_missing_file_text'];
          break;
        case 'embed-block':
          translation = pasteTranslations['pageview_paste_missing_embed_text'];
          break;
        case 'image-carousel':
          translation = pasteTranslations['pageview_paste_missing_carousel_text'];
          break;
      }


      $(workEl).replaceWith('<p><b>[' + translation.toUpperCase() + ']</b></p>');
      /* jshint ignore: end */
    },

    // Since bulb doesn't support certain types of header elements, we should handle them here in case they're used
    // in the source document that is being pasted over.
    handleHeadingElementsOnPaste: function (workEl) {
      var $headerEle = $(workEl);
      var headerTagName = $headerEle.prop('tagName');

      switch (headerTagName) {
        // We need to convert h1 to h2.
        case 'H1':
          $headerEle.replaceWith('<h2>' + $headerEle.text() + '</h2>');
          break;
        case 'H4':
          $headerEle.replaceWith('<h3>' + $headerEle.text() + '</h3>');
          break;
        case 'H5':
        case 'H6':
          $headerEle.replaceWith('<p>' + $headerEle.html() + '</p>');
          break;
      }
    },

    removeAllStyles: function (workEl) {
      $(workEl).removeAttr('style id dir align size width height target');
    },

    /**
     * Reset the formatting before pasting the content.
     */
    resetFormattingBeforePaste: function () {
      var selection = this.document.getSelection();
      if (selection && selection.focusNode) {
        const removeFormatting = ['H2', 'H3', 'B'];
        if (removeFormatting.indexOf(selection.focusNode.nodeName) >= 0) {
          $(selection.focusNode).replaceWith('<p>' + $(selection.focusNode).html() + '</p>');
        } else if (removeFormatting.indexOf(selection.focusNode.parentNode.nodeName) >= 0) {
          $(selection.focusNode.parentNode).replaceWith('<p>' + $(selection.focusNode.parentNode).html() + '</p>');
        }
      }
    }
  });

  return {
    CreateExtension: function CreateExtension(options) {
      return new BulbPasteExtension(options);
    },
  };
}
